import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/SupabaseClient";
import FaceLoginCamera from "../components/FaceLoginCamera";

export default function CandidateLogin() {
    const [identifier, setIdentifier] = useState("candidate1@secureidtechnologies.com");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [cameraEnabled, setCameraEnabled] = useState(false);
    const [voiceEnabled, setVoiceEnabled] = useState(false);
    const [method, setMethod] = useState<"none" | "otp" | "face" | "voice">("none");

    const navigate = useNavigate();
    const apiBase = import.meta.env.VITE_API_BASE;

    /** ✅ Request 6-digit PIN */
    async function requestPin() {
        try {
            setLoading(true);
            setMessage("");

            if (!identifier) throw new Error("Please enter your email or phone number.");

            if (identifier.includes("@")) {
                const { error } = await supabase.auth.signInWithOtp({
                    email: identifier,
                    options: { shouldCreateUser: false },
                });
                if (error) throw error;

                setMessage("📧 A 6-digit verification code has been sent to your email.");
                localStorage.setItem("candidateEmail", identifier);
                setTimeout(() => navigate("/candidate/verify"), 1200);
            } else {
                setMessage("⚠️ Phone login not yet supported.");
            }
        } catch (err: any) {
            console.error("PIN request error:", err);
            setMessage("❌ " + err.message);
        } finally {
            setLoading(false);
        }
    }

    /** ✅ Face capture event */
    const handleFaceCapture = async (blob: Blob) => {
        try {
            const formData = new FormData();
            formData.append("Image", blob, "face.jpg");

            const res = await fetch(`${apiBase}/api/face/match?email=${encodeURIComponent(identifier)}`, {
                method: "POST",
                body: formData,
            });

            const data = await res.json();
            if (data.match) {
                setMessage("✅ Face verified! Redirecting...");
                setTimeout(() => navigate("/candidate/dashboard"), 1000);
            } else {
                setMessage("❌ Face not recognized. Try another method.");
            }
        } catch (err) {
            console.error("Face verification error:", err);
            setMessage("⚠️ Face verification failed.");
        }
    };

    /** 🎤 Placeholder for voice recognition */
    const handleVoiceLogin = async () => {
        setVoiceEnabled(true);
        setMessage("🎤 Voice recognition feature is under development.");
    };

    return (
        <div className="min-h-screen flex flex-col justify-center items-center bg-gradient-to-b from-indigo-700 via-indigo-600 to-violet-600 px-4">
            <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-8">
                <div className="text-center mb-6">
                    <img
                        src="/Images/ExamsPro.png"
                        alt="ExamsPro Logo"
                        className="w-14 h-14 mx-auto mb-2"
                    />
                    <h1 className="text-2xl font-bold text-indigo-700">Exam Taker Login</h1>
                    <p className="text-gray-500 text-sm mt-1">SecureID Technologies – ExamsPro</p>
                </div>

                {/* Step 1 — Candidate ID */}
                <div className="space-y-4 mb-6">
                    <input
                        type="text"
                        placeholder="Email or Phone"
                        className="w-full border border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-200 px-3 py-2 rounded-md outline-none transition"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                    />
                </div>

                {/* Step 2 — Choose method */}
                {method === "none" && (
                    <div className="space-y-4 text-center">
                        <button
                            onClick={() => {
                                setMethod("otp");
                                requestPin();
                            }}
                            disabled={loading}
                            className="w-full bg-indigo-600 text-white py-2 rounded-md font-medium shadow hover:bg-indigo-700 transition"
                        >
                            {loading ? "Sending PIN..." : "Request 6-Digit PIN"}
                        </button>

                        <button
                            onClick={() => setMethod("face")}
                            className="w-full bg-violet-600 text-white py-2 rounded-md font-medium shadow hover:bg-violet-700 transition"
                        >
                            Use Face Recognition
                        </button>

                        <button
                            onClick={handleVoiceLogin}
                            className="w-full bg-pink-600 text-white py-2 rounded-md font-medium shadow hover:bg-pink-700 transition"
                        >
                            Use Voice Recognition
                        </button>
                    </div>
                )}

                {/* Face recognition mode */}
                {method === "face" && !cameraEnabled && (
                    <div className="text-center mt-6 space-y-3">
                        <button
                            onClick={() => setCameraEnabled(true)}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700 transition"
                        >
                            Enable Camera
                        </button>
                        <p className="text-sm text-gray-600">
                            Tap “Enable Camera” to start face verification.
                        </p>
                        <button
                            onClick={() => setMethod("none")}
                            className="text-sm text-indigo-600 hover:underline"
                        >
                            ← Back
                        </button>
                    </div>
                )}

                {cameraEnabled && <FaceLoginCamera onCapture={handleFaceCapture} />}

                {/* Voice mode placeholder */}
                {voiceEnabled && (
                    <div className="mt-6 text-center text-sm text-gray-600">
                        🎙️ Listening for your voice sample... <br />
                        <button
                            onClick={() => setVoiceEnabled(false)}
                            className="mt-2 text-indigo-600 hover:underline"
                        >
                            Cancel
                        </button>
                    </div>
                )}

                {/* Message */}
                {message && (
                    <p className="mt-5 text-sm text-center text-gray-700 whitespace-pre-line">
                        {message}
                    </p>
                )}

                {/* Back link */}
                <div className="mt-8 text-center">
                    <Link
                        to="/solutions/examspro"
                        className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-indigo-600 transition"
                    >
                        <span className="text-lg leading-none">←</span>
                        <span>Back to ExamsPro</span>
                    </Link>
                </div>
            </div>
        </div>
    );
}
